/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIModal from 'UIComponents/dialog/UIModal';
import promiseDone from 'hs-promise-utils/promiseDone';
import { isFailed, isSucceeded } from 'conversations-async-data/async-data/operators/statusComparators';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import { usePromptDismissal } from '../../contextual-prompts/hooks/usePromptDismissals';
import { UserPromptType } from '../../contextual-prompts/constants/userPromptTypes';
import { getDismissedAt } from '../../contextual-prompts/operators/userPromptDataGetters';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIButton from 'UIComponents/button/UIButton';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { useHasConfiguredSLAs } from '../hooks/useHasConfiguredSLAs';
import { getCurrentInboxId } from 'conversations-inbox-lib/unified-inbox/public/selectors';
import { useSelector } from 'react-redux';
import { getSLAConfigUrl } from '../../urls/getSLAConfigUrl';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import { useState, useRef, useEffect } from 'react';
import { clickDismissSLAConfigModal, clickSetupSLAs, viewSLAConfigModal } from '../constants/Events';
import { trackEvent, trackEventBeforeUnload } from '../../usage-tracking/clients/usageTracker';
import UILink from 'UIComponents/link/UILink';
import { KnowledgeBaseLinks } from '../constants/ExternalLinks';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import SLAFeaturePreview from './SLAFeaturePreview';

// Ensures modals z-index is greater than any tooltips z-index in the background. So there is no overlap
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledModal = styled(UIModal).withConfig({
  displayName: "SLAConfigOnboardingModal__StyledModal",
  componentId: "syv3t7-0"
})(["z-index:1112 !important;"]);
const SLAConfigOnboardingModal = () => {
  const hasTrackedModalView = useRef(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const defaultInboxId = useSelector(getCurrentInboxId);
  const {
    asyncPromptData,
    dismissPrompt
  } = usePromptDismissal(UserPromptType.ENABLE_SLA_IN_INBOX);
  const {
    hasConfiguredSLAs,
    loading,
    error
  } = useHasConfiguredSLAs();

  // sets 'isDismissing' to false before unmounting to prevent possible memory leak
  useEffect(() => () => {
    setIsDismissing(false);
  }, []);
  const hasResolvedRequests = isSucceeded(asyncPromptData) && !loading && !error;
  const userPromptData = getData(asyncPromptData);
  if (!hasResolvedRequests || !userPromptData) {
    return null;
  }
  const shouldHideModal = hasConfiguredSLAs === true || getDismissedAt(userPromptData) > 0;
  if (shouldHideModal) {
    return null;
  }
  if (!hasTrackedModalView.current) {
    trackEvent(viewSLAConfigModal());
    hasTrackedModalView.current = true;
  }
  const handleConfirm = () => {
    setIsDismissing(true);
    trackEventBeforeUnload(clickSetupSLAs());
    promiseDone(dismissPrompt().then(() => {
      if (defaultInboxId !== null) {
        window.location.href = getSLAConfigUrl({
          inboxId: defaultInboxId
        });
      }
    }).catch(dismissError => {
      reportError({
        error: dismissError
      });
    }));
  };
  const handleClose = () => {
    trackEvent(clickDismissSLAConfigModal());
    dismissPrompt();
  };
  return /*#__PURE__*/_jsxs(StyledModal, {
    "data-test-id": "selenium-sla-config-onboarding-modal",
    width: 600,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx("h2", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-inbox-ui.sla-onboarding.SLAConfigOnboardingModal.title"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: handleClose
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      children: [/*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "conversations-inbox-ui.sla-onboarding.SLAConfigOnboardingModal.description_jsx",
          elements: {
            UILink
          },
          options: {
            href: KnowledgeBaseLinks.INBOX_SLA,
            external: true
          }
        })
      }), /*#__PURE__*/_jsx(SLAFeaturePreview, {})]
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UILoadingButton, {
        failed: isFailed(asyncPromptData),
        loading: isDismissing,
        use: "primary",
        onClick: handleConfirm,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-inbox-ui.sla-onboarding.SLAConfigOnboardingModal.confirm"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: handleClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "conversations-inbox-ui.sla-onboarding.SLAConfigOnboardingModal.cancel"
        })
      })]
    })]
  });
};
SLAConfigOnboardingModal.displayName = 'SLAConfigOnboardingModal';
export default SLAConfigOnboardingModal;